/**
 * Game sounds, derived by diffing successive engine states (docs/AUDIO.md,
 * "Suggested event mapping for the game screen").
 *
 * The engine knows nothing about sound. After every state change the hook
 * calls diffAudioCues(prev, next) and gets back the cues that change implies,
 * each stamped with the engine time it should play at — so a landing thud
 * waits for the block to land, a level-up arpeggio waits for the banner, and
 * pausing (which stops the engine clock) holds pending cues too.
 */
import type { GameState, Resolution } from '@dropstack/core';
import type { SfxName } from '../contracts.ts';
import { isDanger } from './playfield.ts';
import { landingDelayMs, resolveEndsAt } from './timeline.ts';

export interface AudioCue {
  sfx: SfxName;
  /** Engine time (ms) to play at. */
  at: number;
}

/** The danger tick plays at most this often (engine ms), however long the stack stays high. */
export const DANGER_MIN_INTERVAL_MS = 5000;

/** The game-over cadence follows the final brick's landing by this much. */
export const GAME_OVER_CUE_DELAY_MS = 250;

/** What the differ remembers between calls. */
export interface CueMemo {
  /** Engine time of the last danger tick. */
  lastDangerAt: number;
}

export const INITIAL_CUE_MEMO: CueMemo = { lastDangerAt: Number.NEGATIVE_INFINITY };

function resolutionCues(state: GameState, res: Resolution): AudioCue[] {
  const land = res.at + landingDelayMs(res.kind);
  switch (res.kind) {
    case 'correct':
      return [
        { sfx: 'correct', at: res.at },
        { sfx: res.brokeLayer ? 'layerBreak' : 'cleanFloor', at: land },
      ];
    case 'wrong':
    case 'timeout': {
      const cues: AudioCue[] = [
        { sfx: res.kind, at: res.at },
        { sfx: 'brickLand', at: land },
      ];
      if (state.phase === 'gameOver') cues.push({ sfx: 'gameOver', at: land + GAME_OVER_CUE_DELAY_MS });
      return cues;
    }
  }
}

/**
 * Cues implied by the change from `prev` to `next` (prev null = the run just
 * started), sorted by time.
 *
 * - a new resolution → correct + layerBreak/cleanFloor, or wrong/timeout + brickLand
 *   (+ gameOver if it ended the run);
 * - a checkpoint that changed the level → levelUp/levelDown when the banner appears;
 * - a new falling block → spawn, and a soft danger tick when the stack is high
 *   (rate-limited through `memo`).
 * Quitting (endGame) is silent: the player chose to stop.
 */
export function diffAudioCues(prev: GameState | null, next: GameState, memo: CueMemo): { cues: AudioCue[]; memo: CueMemo } {
  const cues: AudioCue[] = [];
  let lastDangerAt = memo.lastDangerAt;

  const res = next.lastResolution;
  if (res && res.problem.id !== prev?.lastResolution?.problem.id) {
    cues.push(...resolutionCues(next, res));
    const change = next.pendingLevelChange;
    if (change && change !== prev?.pendingLevelChange && next.phase !== 'gameOver') {
      cues.push({ sfx: change.levelAfter > change.levelBefore ? 'levelUp' : 'levelDown', at: resolveEndsAt(next, res) });
    }
  }

  const block = next.active;
  if (block && block.problem.id !== prev?.active?.problem.id) {
    cues.push({ sfx: 'spawn', at: block.spawnedAt });
    if (isDanger({ bricks: block.bricksAtSpawn, config: next.config }) && block.spawnedAt - lastDangerAt >= DANGER_MIN_INTERVAL_MS) {
      cues.push({ sfx: 'danger', at: block.spawnedAt });
      lastDangerAt = block.spawnedAt;
    }
  }

  cues.sort((a, b) => a.at - b.at);
  return { cues, memo: lastDangerAt === memo.lastDangerAt ? memo : { lastDangerAt } };
}

/** Split a queue into the cues due by `now` and the ones still waiting. Keeps order. */
export function takeDueCues(queue: readonly AudioCue[], now: number): { due: AudioCue[]; waiting: AudioCue[] } {
  const due: AudioCue[] = [];
  const waiting: AudioCue[] = [];
  for (const cue of queue) (cue.at <= now ? due : waiting).push(cue);
  return { due, waiting };
}
